<template>
  <div class="deliverables-form-wrap">
    <SrpDetailsSummary :isOpened="isOpened" @update:isOpened="isOpened = $event">
      <template #heading>
        <div class="global-h4">
          <span><span>Deliverables</span></span>
        </div>
      </template>

      <template #details>
        <!-- Deliverables form -->
        <div class="deliverables-form">
          <!--<div class="global-h5 deliverables-form__title">Deliverables</div>-->
          <input
            class="global-text-input deliverables-form__download-link-input"
            v-for="(_, index) in howManyFieldsVisible"
            :key="`deliverables-link-${index}`"
            type="text"
            placeholder="drive.google.com/..."
            v-model="props.deliverables[index]"
            :disabled="props.isSaving"
          />

          <LinkWithIcon
            class="deliverables-form__add-link"
            isDottedUnderline
            iconInset="auto -3px auto auto"
            @click="
              () => {
                if (props.deliverables.length < maxNumberOfFields) {
                  howManyFieldsVisible += 1;
                }
              }
            "
            :isDisabled="howManyFieldsVisible === maxNumberOfFields || props.isSaving"
          >
            <template #icon><IconEmbedded name="plus_3" :size="18" /></template>
            <span>add another</span>
          </LinkWithIcon>

          <div v-if="isValidationErrorNotesVisible" class="global-error-string" style="margin-bottom: 10px">Please add links to the deliverables (or describe them if no links are available)</div>

          <SrpButton
            v-if="!deliverablesAreReadyDate"
            class="deliverables-form__is-ready-checkbox"
            :isDisabled="props.isSaving"
            @click="handleDeliverablesReadyClick"
            :fill="hasSetDeliverablesLinks ? 'solid' : 'outlined'"
          >
            Deliverables are ready
          </SrpButton>
          <NoteWithIcon v-else color="green" size="tiny" style="width: 100%">
            <template #text><b>Deliverables sent for review</b></template>
          </NoteWithIcon>
        </div>
        <!-- / Deliverables form -->
      </template>
    </SrpDetailsSummary>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";

// Components
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";

// Contracts
import { CustomCollabStatuses } from "@contracts/customCollabs";
import NoteWithIcon from "@components/NoteWithIcon.vue";

const props = withDefaults(
  defineProps<{
    deliverablesAreReadyDate: string | null;
    deliverables: Array<string>;
    isSaving: boolean;
    customCollabStatus: string;
  }>(),
  {
    deliverablesAreReadyDate: null,
    deliverables: () => [],
    isSaving: false,
    customCollabStatus: "",
  }
);

const emit = defineEmits<{
  (event: "click:deliverablesAreReady"): void;
  (event: "update:deliverables", value: Array<string>): void;
}>();

// Link field values ==========================================================
const maxNumberOfFields = 20;
const howManyFieldsVisible = ref<number>(1);
const isOpened = ref<boolean>(false);
const isValidationErrorNotesVisible = ref<boolean>(false);

// Computed property to check if there are any non-empty deliverables
const hasSetDeliverablesLinks = computed(() => {
  return props.deliverables.some(link => link.trim() !== "");
});

onMounted(() => {
  howManyFieldsVisible.value = props.deliverables.length || 1;
  checkOpenForm(props.customCollabStatus);
});

// Open Form check ============================================================
function checkOpenForm(status: string) {
  if (props.deliverablesAreReadyDate || status === CustomCollabStatuses.PreparingDeliverables) {
    isOpened.value = true;
  }
}

watch(
  () => props.customCollabStatus,
  newStatus => {
    checkOpenForm(newStatus);
  }
);

// Deliverables Ready click ==================================================
function handleDeliverablesReadyClick() {
  if (hasSetDeliverablesLinks.value) {
    isValidationErrorNotesVisible.value = false;
    emit("click:deliverablesAreReady");
  } else {
    isValidationErrorNotesVisible.value = true;
  }
}
// Watch for changes in deliverables and reset validation error visibility
watch(
  () => props.deliverables,
  () => {
    isValidationErrorNotesVisible.value = false;
  },
  { deep: true }
);
</script>

<style scoped lang="scss">
// Deliverables form ==========================================================
.deliverables-form {
  padding-top: 15px;
  border-top: 1px rgba(0, 0, 0, 0.1) solid;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &__title {
    margin-bottom: 6px;
    font-weight: 400;
  }

  &__download-link-input {
    width: 100%;
    max-width: 600px;
    margin-bottom: 6px;
  }

  &__add-link {
    margin: 0 0 16px -7px;
  }

  &__is-ready-checkbox {
  }
}

// Deliverables form wrap =====================================================
.deliverables-form-wrap {
  padding: 14px 20px 16px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.08);
}
</style>
